/**
 * Where the browser learns which pool and which API it is talking to.
 *
 * **Baked in at build time, from stack outputs, by the deploy workflow.** These
 * are not values anyone types. `deploy.yml` already reads every output of
 * `Platform-{environment}` before it builds, so the pool id, client id and API
 * URL come from the stack that created them and cannot drift from it. The same
 * rule as everywhere else in this pipeline: no human types a value the account
 * already knows.
 *
 * **None of this is secret.** A Cognito user pool id and a public web client id
 * appear in every browser that has ever loaded a Cognito app, and the API URL is
 * the thing the browser calls. The client can create a user and sign one in;
 * what it cannot do is write `custom:tenantId`, which is the attribute that
 * would let someone issue themselves a token for another workspace. That
 * restriction lives in `cognito-auth.ts` and is asserted in
 * `platform-stack.test.ts` — not here.
 *
 * A missing value throws at startup rather than at the first sign-in attempt. A
 * shell that renders a login form it cannot possibly submit is worse than one
 * that says what is wrong.
 */

interface WebConfig {
  readonly userPoolId: string;
  readonly webClientId: string;
  readonly apiUrl: string;
}

const read = (name: string, value: string | undefined): string => {
  if (value === undefined || value.trim() === "") {
    throw new Error(
      `${name} was not set when this bundle was built. It comes from a ` +
        `Platform-* stack output via .github/workflows/deploy.yml — a local ` +
        `build needs it in web/.env.local. See docs/ENVIRONMENTS.md.`,
    );
  }
  return value.trim();
};

/**
 * Read eagerly, so a misbuilt bundle fails on load with a message that names
 * the missing variable. Vite replaces `import.meta.env.VITE_*` at build time,
 * so these are literals in the output — there is nothing to look up at runtime
 * and nothing to go wrong later.
 */
export const config: WebConfig = {
  userPoolId: read("VITE_USER_POOL_ID", import.meta.env.VITE_USER_POOL_ID),
  webClientId: read("VITE_WEB_CLIENT_ID", import.meta.env.VITE_WEB_CLIENT_ID),
  apiUrl: read("VITE_API_URL", import.meta.env.VITE_API_URL),
};
